import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import AnimateHeight from 'react-animate-height'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { brandCurrencySuffixes, isRailBookers } from '../../configuration/brand'
import { getBrand } from '../../redux/reducers/configuration'
import { getSearchResult } from '../../redux/reducers/searchResults'
import { recordClickEvent } from '../../utilities/algolia/algolia'
import { addBrandCurrencyPrefix } from '../../utilities/currency/formatPrice'
import '../../styles/scss/components/trip-card.scss'

const TRUNCATED_DESTINATIONS_SIZE = isRailBookers() ? 100 : 94

const TripCard = ({ trip }) => {
  const destinationsRef = useRef()
  const brand = useSelector(getBrand)
  const searchResult = useSelector(state => getSearchResult(state, trip.id))

  const [destinationsOpen, setDestinationsOpen] = useState(false)
  const [displayDestinationToggle, setDisplayDestinationToggle] = useState(false)
  const [mouseOverImage, setMouseOverImage] = useState(false)

  const onClickTripCard = useCallback((event) => {
    recordClickEvent(searchResult)
  }, [searchResult])

  useEffect(() => {
    // destinations are not fully rendered on mount so timeout is required
    setTimeout(() => onWindowResize(), 250)
    window.addEventListener("resize", onWindowResize)
    return () => window.removeEventListener("resize", onWindowResize)
  }, [])

  function onWindowResize() {
    const destinationsHeight = _.get(destinationsRef, 'current.offsetHeight')
    if (destinationsHeight) {
      setDisplayDestinationToggle(destinationsHeight > TRUNCATED_DESTINATIONS_SIZE)
    }
  }

  function onMouseOutImage() {
    setMouseOverImage(false)
  }

  function onMouseOverImage(event) {
    const windowWidth = _.get(event, 'view.innerWidth')
    if (windowWidth > 768) {
      setMouseOverImage(true)
    }
  }

  function toggleDestinations() {
    setDestinationsOpen(!destinationsOpen)
  }

  return (
    <div className="trip-card">
      <div className="trip-card-container">
        <a 
          aria-label={_.get(trip, 'title')}
          className="u-flex trip-card-hero-image"
          href={_.get(trip, 'url')}
          onClick={onClickTripCard}
          onMouseOver={onMouseOverImage}
          onMouseOut={onMouseOutImage}
        >
          <img
            alt=""
            className="u-flex trip-card-hero-image hero-image-underlay"
            src={_.get(trip, 'map') || _.get(trip, 'image')}
          />
          <AnimateHeight
            height={mouseOverImage ? 0 : 'auto' }
            style={{
              borderRadius: isRailBookers() ? 0 : '12px 12px 0 0',
              width: '100%',
              zIndex: 2,
            }}
          >
            <div
              className="u-flex trip-card-hero-image hero-image-overlay"
              style={{ backgroundImage: `url("${_.get(trip, 'image') || _.get(trip, 'map')}")`}}
            >
              {_.get(trip, 'offer') && (
                <span className="hero-offer">
                  {_.get(trip, 'offer')}
                </span>
              )}
              <div className="hero-image-gradient" />
              <span className="u-flex-align-self-end hero-title">
                {_.get(trip, 'title')}
              </span>
            </div>
          </AnimateHeight>
        </a>
        <div className="u-flex u-flex-column trip-card-content-container">
          <AnimateHeight height={destinationsOpen ? 'auto' : TRUNCATED_DESTINATIONS_SIZE - 4}>
            <div
              className="u-flex u-flex-wrap trip-card-destination-carets"
              ref={destinationsRef}
            >
              {_.map(_.get(trip, 'displayDestinations', []), (destination, index) => (
                <Fragment key={`${index}`}>
                  {index > 0 && <div />}
                  <span>{destination}</span>
                </Fragment>
              ))}
            </div>
          </AnimateHeight>
          <button
            aria-label={destinationsOpen ? 'Less' : 'More'}
            className="u-flex trip-card-more-prompt"
            disabled={!displayDestinationToggle}
            onClick={toggleDestinations}
          >
            {displayDestinationToggle
              ? destinationsOpen ? 'Less' : 'More'
              : ''}
            <span className={destinationsOpen ? 'more-toggle-open' : 'more-toggle-close'} />
          </button>
          <div className="trip-card-spacer"/>
          {_.get(trip, "tripTag") && (
            <div className="trip-card-signature-premier" style={displayDestinationToggle ? {marginTop: 20} : null}>
              <img src={_.get(trip, "tripTag.image.path")} alt={_.get(trip, "tripTag.image.caption")}/>
              <span dangerouslySetInnerHTML={{__html: _.get(trip, 'tripTag.title') }} />
            </div>
          )}
          <div className="u-flex u-flex-space-between u-flex-wrap trip-card-footer">
            <div
              aria-label={`${_.get(trip, 'duration')} Days from ${_.get(trip, 'advertisedPrice')}`}
              className="u-flex u-flex-wrap trip-card-duration-price"
            >
              {_.get(trip, 'duration')}
              <span className="trip-card-days"> Days</span>
              <span className="trip-card-from">   From </span>
              <span className="trip-card-price">
                {addBrandCurrencyPrefix(brand, _.get(trip, 'advertisedPrice'))}
                <span className={brandCurrencySuffixes[brand] ? 'trip-card-price-suffix' : 'trip-card-price-no-suffix'}>
                  <span className="trip-card-price-suffix-text">{brandCurrencySuffixes[brand]}</span> 
                  <span className="trip-card-price-suffix-text">pp</span>
                </span>
              </span>
            </div>
            <a
              className="btn trip-card-details-button"
              href={_.get(trip, 'url')}
              onClick={onClickTripCard}
            >
              Trip Details
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

TripCard.propTypes = {
  trip: PropTypes.shape({
    advertisedPrice: PropTypes.number,
    destinations: PropTypes.arrayOf(PropTypes.string),
    duration: PropTypes.number,
    image: PropTypes.string,
    map: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
}

export default TripCard
